import React, { useState, useEffect, useRef, useCallback } from 'react';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

export default function UpNext() {
    const [events, setEvents] = useState([]);
    const [selectedEventID, setSelectedEventID] = useState('');
    const [competitions, setCompetitions] = useState([]);
    const [selectedCompetitionID, setSelectedCompetitionID] = useState('');
    const [competitors, setCompetitors] = useState([]);
    const [registrations, setRegistrations] = useState([]);
    const [scores, setScores] = useState([]);
    const [recentCompetitors, setRecentCompetitors] = useState([]);
    const [upcomingCompetitors, setUpcomingCompetitors] = useState([]);
    const [slideshowMode, setSlideshowMode] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const refreshTimer = useRef(null);
    
    // Fetch events when component loads
    useEffect(() => {
        const fetchEvents = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/getAllEvents`);
                const data = await response.json();
                const enabledEvents = Array.isArray(data) ? data.filter(event => event.Enabled) : [];
                setEvents(enabledEvents);
                
                // Auto-select first event
                if (enabledEvents.length > 0) {
                    setSelectedEventID(enabledEvents[0].EventID);
                }
            } catch (error) {
                console.error('Error fetching events:', error);
            }
        };
        fetchEvents();
    }, []);

    // Fetch competitions when an event is selected
    useEffect(() => {
        if (!selectedEventID) return;
        
        const fetchCompetitions = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/getCompetitions?EventID=${selectedEventID}`);
                const data = await response.json();
                // Filter only competitions with UpNextEnabled
                const enabledCompetitions = Array.isArray(data) ? data.filter(comp => comp.UpNextEnabled) : [];
                setCompetitions(enabledCompetitions);
                
                // Reset competition selection
                setSelectedCompetitionID('');
            } catch (error) {
                console.error('Error fetching competitions:', error);
                setCompetitions([]);
            }
        };
        
        fetchCompetitions();
    }, [selectedEventID]);

    // Fetch competitors, registrations, and scores when competition is selected
    useEffect(() => {
        if (!selectedEventID || !selectedCompetitionID) {
            setCompetitors([]);
            setRegistrations([]);
            setScores([]);
            setRecentCompetitors([]);
            setUpcomingCompetitors([]);
            return;
        }
        
        setIsLoading(true);
        
        const fetchData = async () => {
            try {
                // Fetch all competitors for the selected event
                const competitorsResponse = await fetch(`${API_BASE_URL}/getAllCompetitors?EventID=${selectedEventID}`);
                const competitorsData = await competitorsResponse.json();
                const allCompetitors = Array.isArray(competitorsData) ? competitorsData : [];
                setCompetitors(allCompetitors);
                
                // Fetch registrations if needed
                const selectedCompetition = competitions.find(c => c.CompetitionID === Number(selectedCompetitionID));
                let relevantCompetitors = allCompetitors;
                
                // Registration check is skipped if RegistrationRequired is false
                if (selectedCompetition && selectedCompetition.RegistrationRequired) {
                    const registrationsResponse = await fetch(`${API_BASE_URL}/getRegistrations?EventID=${selectedEventID}`);
                    const registrationsData = await registrationsResponse.json();
                    const competitionRegistrations = Array.isArray(registrationsData) 
                        ? registrationsData.filter(r => r.CompetitionID === Number(selectedCompetitionID))
                        : [];
                    setRegistrations(competitionRegistrations);
                    
                    // Filter competitors to only those registered for this competition
                    const registeredCompetitorIDs = competitionRegistrations.map(r => r.CompetitorID);
                    relevantCompetitors = allCompetitors.filter(c => registeredCompetitorIDs.includes(c.CompetitorID));
                }
                
                // Fetch scores for the selected competition
                const scoresResponse = await fetch(`${API_BASE_URL}/getScores?EventID=${selectedEventID}&CompetitionID=${selectedCompetitionID}`);
                const scoresData = await scoresResponse.json();
                const competitionScores = Array.isArray(scoresData) ? scoresData : [];
                setScores(competitionScores);
                
                // Process data to find recent and upcoming competitors
                processCompetitorData(relevantCompetitors, competitionScores);
                
            } catch (error) {
                console.error('Error fetching data:', error);
                setCompetitors([]);
                setRegistrations([]);
                setScores([]);
                setRecentCompetitors([]);
                setUpcomingCompetitors([]);
            } finally {
                setIsLoading(false);
            }
        };
        
        fetchData();
    }, [selectedEventID, selectedCompetitionID, competitions]);

    // Process competitor data to determine recent and upcoming competitors
    const processCompetitorData = (competitors, scores) => {
        if (!Array.isArray(competitors) || competitors.length === 0) {
            setRecentCompetitors([]);
            setUpcomingCompetitors([]);
            return;
        }
        
        // Get unique competitor IDs that have scores
        const scoredCompetitorIDs = [...new Set(scores.map(s => s.CompetitorID))];
        
        // Separate competitors into scored and unscored
        const scoredCompetitors = competitors.filter(c => scoredCompetitorIDs.includes(c.CompetitorID))
            .sort((a, b) => b.CompetitorID - a.CompetitorID); // Sort in descending order by ID
        
        const unscoredCompetitors = competitors.filter(c => !scoredCompetitorIDs.includes(c.CompetitorID))
            .sort((a, b) => a.CompetitorID - b.CompetitorID); // Sort in ascending order by ID
        
        // Get the last 5 scored competitors (highest IDs)
        const recent = scoredCompetitors.slice(0, 5);
        
        // Get the next 5 competitors (lowest IDs among unscored)
        const upcoming = unscoredCompetitors.slice(0, 5);
        
        setRecentCompetitors(recent);
        setUpcomingCompetitors(upcoming);
    };

    // Add a function to fetch data for the current competition
    const fetchCompetitorData = useCallback(async () => {
        if (!selectedEventID || !selectedCompetitionID) return;
        
        setIsLoading(true);
        
        try {
            // Fetch all competitors for the selected event
            const competitorsResponse = await fetch(`${API_BASE_URL}/getAllCompetitors?EventID=${selectedEventID}`);
            const competitorsData = await competitorsResponse.json();
            const allCompetitors = Array.isArray(competitorsData) ? competitorsData : [];
            setCompetitors(allCompetitors);
            
            // Fetch registrations if needed
            const selectedCompetition = competitions.find(c => c.CompetitionID === Number(selectedCompetitionID));
            let relevantCompetitors = allCompetitors;
            
            // Registration check is skipped if RegistrationRequired is false
            if (selectedCompetition && selectedCompetition.RegistrationRequired) {
                const registrationsResponse = await fetch(`${API_BASE_URL}/getRegistrations?EventID=${selectedEventID}`);
                const registrationsData = await registrationsResponse.json();
                const competitionRegistrations = Array.isArray(registrationsData) 
                    ? registrationsData.filter(r => r.CompetitionID === Number(selectedCompetitionID))
                    : [];
                setRegistrations(competitionRegistrations);
                
                // Filter competitors to only those registered for this competition
                const registeredCompetitorIDs = competitionRegistrations.map(r => r.CompetitorID);
                relevantCompetitors = allCompetitors.filter(c => registeredCompetitorIDs.includes(c.CompetitorID));
            }
            
            // Fetch scores for the selected competition
            const scoresResponse = await fetch(`${API_BASE_URL}/getScores?EventID=${selectedEventID}&CompetitionID=${selectedCompetitionID}`);
            const scoresData = await scoresResponse.json();
            const competitionScores = Array.isArray(scoresData) ? scoresData : [];
            setScores(competitionScores);
            
            // Process data to find recent and upcoming competitors
            processCompetitorData(relevantCompetitors, competitionScores);
            
        } catch (error) {
            console.error('Error fetching data:', error);
            setCompetitors([]);
            setRegistrations([]);
            setScores([]);
            setRecentCompetitors([]);
            setUpcomingCompetitors([]);
        } finally {
            setIsLoading(false);
        }
    }, [selectedEventID, selectedCompetitionID, competitions]);

    // Replace the existing slideshow useEffect with a more general refresh timer
    useEffect(() => {
        if (!selectedCompetitionID) return;
        
        // Clear any existing timer
        if (refreshTimer.current) {
            clearInterval(refreshTimer.current);
        }
        
        // Set up a new timer for some seconds
        refreshTimer.current = setInterval(async () => {
            if (slideshowMode) {
                // Rotate through enabled competitions
                const enabledCompetitions = competitions.filter(comp => comp.UpNextEnabled);
                if (enabledCompetitions.length <= 1) {
                    // If there's only one competition, just refresh the data
                    await fetchCompetitorData();
                } else {
                    // Find the index of the current competition
                    const currentIndex = enabledCompetitions.findIndex(c => c.CompetitionID === Number(selectedCompetitionID));
                    
                    // Calculate the index of the next competition
                    const nextIndex = (currentIndex + 1) % enabledCompetitions.length;
                    
                    // Set the next competition
                    const nextCompetitionID = enabledCompetitions[nextIndex].CompetitionID.toString();
                    setSelectedCompetitionID(nextCompetitionID);
                }
            } else {
                // Not in slideshow mode, just refresh the current competition's data
                await fetchCompetitorData();
            }
        }, 30000); // seconds
        
        return () => {
            if (refreshTimer.current) {
                clearInterval(refreshTimer.current);
            }
        };
    }, [selectedCompetitionID, competitions, slideshowMode, fetchCompetitorData]);

    // Handle competition change
    const handleCompetitionChange = (e) => {
        const competitionID = e.target.value;
        setSelectedCompetitionID(competitionID);
    };

    // Handle slideshow mode toggle
    const handleSlideshowToggle = (e) => {
        const isChecked = e.target.checked;
        setSlideshowMode(isChecked);
        
        // If enabling slideshow with no competition selected, select the first available one
        if (isChecked && !selectedCompetitionID) {
            const enabledCompetitions = competitions.filter(comp => comp.UpNextEnabled);
            if (enabledCompetitions.length > 0) {
                setSelectedCompetitionID(enabledCompetitions[0].CompetitionID.toString());
            }
        }
    };

    // Helper function to get competitor full name
    const getCompetitorName = (competitor) => {
        return `${competitor.FirstName} ${competitor.LastName}`;
    };

    return (
        <div className="p-1">
            <h2>Up Next / In Progress</h2>
            
            <div className="flex items-center mb-1 gap-1">
                <label>
                    <select 
                        className="form-select" 
                        value={selectedEventID} 
                        onChange={(e) => setSelectedEventID(e.target.value)}
                    >
                        <option value="">Select Event</option>
                        {Array.isArray(events) && events.map(event => (
                            <option key={event.EventID} value={event.EventID}>{event.EventName}</option>
                        ))}
                    </select>
                </label>
                
                <label>
                    <select 
                        className="form-select"
                        value={selectedCompetitionID}
                        onChange={handleCompetitionChange}
                        disabled={!selectedEventID || competitions.length === 0}
                    >
                        <option value="">Select Competition</option>
                        {competitions.map(competition => (
                            <option key={competition.CompetitionID} value={competition.CompetitionID}>
                                {competition.CompetitionName}
                            </option>
                        ))}
                    </select>
                </label>
                
                {selectedEventID && competitions.length > 0 && (
                    <label className="flex items-center">
                        <input 
                            type="checkbox" 
                            className="form-checkbox"
                            checked={slideshowMode}
                            onChange={handleSlideshowToggle}
                        />
                        Looping Slideshow
                    </label>
                )}
            </div>
            
            {selectedCompetitionID && (
                <div className="mt-1">
                    {isLoading ? (
                        <p>Loading data...</p>
                    ) : (
                        <div className="up-next-container">
                            <div className="recently-competed">
                                <h3>Recently Competed</h3>
                                {recentCompetitors.length > 0 ? (
                                    <table className="data-table">
                                        <thead>
                                            <tr>
                                                <th>ID</th>
                                                <th>Name</th>
                                                <th>Organization</th>
                                                <th>Division</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {recentCompetitors.map((competitor, index) => (
                                                <tr 
                                                    key={competitor.CompetitorID} 
                                                    className="recently-scored"
                                                >
                                                    <td>{competitor.CompetitorID}</td>
                                                    <td>{getCompetitorName(competitor)}</td>
                                                    <td className="organization-column">{competitor.Organization}</td>
                                                    <td>{competitor.Division}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                ) : (
                                    <p>No recent competitors found</p>
                                )}
                            </div>
                            
                            <div className="up-next-competitors">
                                <h3>Up Next / In Progress</h3>
                                {upcomingCompetitors.length > 0 ? (
                                    <table className="data-table">
                                        <thead>
                                            <tr>
                                                <th>ID</th>
                                                <th>Name</th>
                                                <th>Organization</th>
                                                <th>Division</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {upcomingCompetitors.map((competitor, index) => (
                                                <tr key={competitor.CompetitorID}>
                                                    <td>{competitor.CompetitorID}</td>
                                                    <td>{getCompetitorName(competitor)}</td>
                                                    <td className="organization-column">{competitor.Organization}</td>
                                                    <td>{competitor.Division}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                ) : (
                                    <p>No upcoming competitors found</p>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
